import { SnackbarType } from 'common/interfaces';
import { useAppDispatch } from 'setup/store';
import { useAppActions } from './app-actions';
import { useTranslation } from 'react-i18next';
import { RoyaltyReportActions } from 'reducers/royalty-report-reducer';
import { IACHFormResponse, IRoyaltyReport } from 'common/interfaces/iRoyaltyReports';
import {
  getACHFormResponses,
  getACHReportDetails,
  getRoyaltyReportsForStore,
  postRoyaltyReportApproval,
  postUpdateACHFormEntry,
} from 'services/royalty-report-service';
import { IStatsSearchCriteria } from 'common/interfaces/idashboard';
import { getDayReportCountStats } from 'services/dashboard-service';

export const useRoyaltyReportActions = () => {
  const dispatch = useAppDispatch();
  const { showSnackBar } = useAppActions();
  const { t } = useTranslation();

  const setLoading = async (isLoading: boolean) => {
    dispatch(RoyaltyReportActions.setLoading(isLoading));
  };

  const updateACHFormResponse = async (payload: IACHFormResponse) => {
    setLoading(true);
    dispatch(RoyaltyReportActions.setError(null));
    resetUpdatedData();

    let resp = await postUpdateACHFormEntry(payload.id, payload);

    console.log('updateACHFormResponse.....', resp);
    setLoading(false);
    if (resp && resp.status.success) {
      dispatch(RoyaltyReportActions.setDataUpdated(resp.data));
      return true;
    } else {
      dispatch(RoyaltyReportActions.setDataUpdated(undefined));
      const msg = resp?.status?.msg || t('error.couldNotUpdate') || '';
      console.log('updateACHFormResponse. setError.....', msg);
      dispatch(RoyaltyReportActions.setError({ code: '', msg }));
    }
    return false;
  };

  const getRoyaltyReportDetails = async (reportId: number) => {
    setLoading(true);
    let resp = await getACHReportDetails(reportId);
    setLoading(false);
    if (resp.status.success) {
      dispatch(
        RoyaltyReportActions.setACHFormResponses({
          data: resp.data.formResponses,
          append: false,
        }),
      );
      dispatch(RoyaltyReportActions.setOngoingReport(resp.data.summary));
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadACHFormResponses') || '');
    }
  };

  const loadRoyaltyReports = async (
    storeId: number,
    startDate: string,
    endDate: string,
    pageNo: number,
    pageSize: number,
  ) => {
    setLoading(true);
    const resp = await getRoyaltyReportsForStore(storeId, startDate, endDate, pageNo, pageSize);
    console.log('loadRoyaltyReports.....', resp?.data);
    if (resp.status.success) {
      dispatch(
        RoyaltyReportActions.setReports({
          data: resp.data,
          append: false,
        }),
      );
      setLoading(false);
      return resp.data;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.loadReports') || '');
    }
    setLoading(false);
  };

  const resetUpdatedData = () => {
    dispatch(RoyaltyReportActions.resetDataUpdated());
  };

  const setOngoingRoyaltyReport = (dayReport: IRoyaltyReport | undefined) => {
    dispatch(RoyaltyReportActions.setOngoingReport(dayReport));
  };

  const approveRoyaltyReport = async (reportId: number) => {
    setLoading(true);
    const resp = await postRoyaltyReportApproval(reportId);
    setLoading(false);
    console.log('approveRoyaltyReport. resp.....', resp?.data);
    if (resp.status.success) {
      return true;
    } else {
      showSnackBar(SnackbarType.ERROR, resp?.status?.msg || t('error.approveReport') || '');
    }
  };

  const loadDayReportCountStats = async (storeId: number, criteria: IStatsSearchCriteria) => {
    setLoading(true);
    const statsResp = await getDayReportCountStats(storeId, criteria);
    setLoading(false);
    if (statsResp?.status && statsResp.data) {
      dispatch(RoyaltyReportActions.setDayReportsStatus(statsResp.data));
    }
    return statsResp.status && statsResp.data ? statsResp.data : undefined;
  };

  const resetContext = async () => {
    dispatch(RoyaltyReportActions.resetContext());
  };

  return {
    setLoading,
    updateACHFormResponse,
    resetUpdatedData,
    loadRoyaltyReports,
    setOngoingRoyaltyReport,
    approveRoyaltyReport,
    loadDayReportCountStats,
    resetContext,
    getRoyaltyReportDetails,
  };
};
